import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { primaryHomeForRoles } from "@/lib/auth";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Setup2FA() {
  const nav = useNavigate();
  const { state } = useLocation();
  const { login, user } = useAuth();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // If backend disabled 2FA globally, redirect out.
  useEffect(() => {
    api.get("/config/public").then((r) => {
      if (r.data?.require_2fa === false) {
        toast.info("2FA deshabilitado en modo demo");
        nav(user ? primaryHomeForRoles(user.roles) : "/login", { replace: true });
      }
    }).catch(() => {});
  }, [nav, user]);

  if (!state?.setup_token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#0B0B0B]">
        <div className="text-center p-8">
          <p>Setup token no disponible.</p>
          <Link to="/login" className="text-[#F26A21] hover:text-[#0B0B0B] mt-4 inline-block">Volver a login</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/2fa/confirm", { setup_token: state.setup_token, totp_code: code });
      login(data);
      toast.success("2FA activado. Bienvenido.");
      nav(primaryHomeForRoles(data.user.roles));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="p-6"><Link to="/"><Logo /></Link></div>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg p-8 bg-[#F9FAFB] border border-gray-200 rounded-2xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#F26A21] font-semibold">
            <ShieldCheck className="w-4 h-4" /> Configura 2FA
          </div>
          <h1 className="mt-2 text-2xl font-bold text-[#0B0B0B] font-display">Escanea el código con tu app</h1>
          <p className="mt-2 text-sm text-gray-600">
            Usa Google Authenticator, 1Password, Authy o cualquier app compatible con TOTP.
            Escanea el QR y luego ingresa el código de 6 dígitos.
          </p>

          <div className="mt-6 flex justify-center">
            <div className="p-3 bg-white rounded-lg">
              <img src={state.qr_data_url} alt="QR TOTP" data-testid="totp-qr" width={220} height={220} />
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="setup-2fa-form">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">Código de 6 dígitos</label>
              <input
                data-testid="setup-2fa-code"
                required maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-3 text-center font-mono text-2xl tracking-widest text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]"
              />
            </div>
            <button type="submit" disabled={submitting || code.length !== 6} data-testid="setup-2fa-submit-btn" className="w-full py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-60 transition-all">
              {submitting ? "Verificando..." : "Confirmar y activar"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
